import React, {useEffect, useState, useCallback, useRef} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MediaService } from '../../services/mediaService';
import { WatchlistService } from '../../services/watchlistService';
import { CuratedListsService } from '../../services/curatedListsService';
import { getCuratedListCover } from '../../utils/curatedListCover';
import { TitleRatingService, ratingErrorMessage } from '../../services/titleRatingService';
import StarRating from '../../components/StarRating/StarRating';
import { RATE_TITLE_INTENT, communityRatingView, isValidRating, optimisticAggregate } from '@aluvo/constants/src/ratings';
import { savePendingIntent } from '../../utils/pendingIntent';
import SeasonsList from '../../components/SeasonsList/SeasonsList';
import { buildVerifyPath } from '../../quiz/pendingQuiz';
import { useAuth } from '../../context/AuthContext';
import AuthModal from '../../components/AuthModal/AuthModal';
import { useAuthGate } from '../../hooks/useAuthGate';
import { AUTH_MESSAGES } from '../../utils/authMessages';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import AppPromotionCard from '../../components/AppPromotionCard/AppPromotionCard';
import styles from './Movie.module.css';
import ProviderSection from "../../components/ProviderSection/ProviderSection";
import CastSection from '../../components/CastSection/CastSection';
import SaveToListModal from '../../components/MyLists/SaveToListModal';
import { shareMedia, trackSharedMediaLinkOpened } from '../../utils/mediaSharing';
import { MediaCollectorStats } from '../../components/CollectorStats/CollectorStats';

/**
 * Detail page for both movies and TV shows.
 * Route params: kind (movie|tv), tmdbId
 */
export default function Movie() {
    const { kind: routeKind, tmdbId, seasonNumber } = useParams();
    const kind = routeKind || 'tv';
    const { user } = useAuth();
    const { gateAction, authModalProps } = useAuthGate(user);
    const navigate = useNavigate();

    const [media, setMedia] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quizLoading, setQuizLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('watch');
    const [inWatchlist, setInWatchlist] = useState(false);
    const [watchlistLoading, setWatchlistLoading] = useState(false);
    // Aluvo star ratings (movies only; independent of coins). Both arrive with
    // the detail payload; writes are optimistic and roll back on failure.
    const [myRating, setMyRating] = useState(null);
    const [aluvoRating, setAluvoRating] = useState(null);
    const [ratingSaving, setRatingSaving] = useState(false);
    const [ratingError, setRatingError] = useState(null); // { message, retry: () => void }
    // Whose view the loaded details reflect ('guest' or a user id), so a
    // resumed rating never races the post-login reload.
    const [detailsViewer, setDetailsViewer] = useState(null);
    const ratingStartedRef = useRef(false);
    const [seriesMasteryProgress, setSeriesMasteryProgress] = useState(null);
    const [seasons, setSeasons] = useState([]);
    const [featuredLists, setFeaturedLists] = useState([]);
    const [countdown, setCountdown] = useState('');
    const [saveToListTarget, setSaveToListTarget] = useState(null);
    const [shareStatus, setShareStatus] = useState(null);
    const [sharing, setSharing] = useState(false);
    const countdownRef = useRef(null);
    const resumedIntentRef = useRef(false);
    const shareStatusTimerRef = useRef(null);

    useEffect(() => {
        const loadMedia = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await MediaService.getDetails(kind, tmdbId);
                console.log('Movie data:', data);
                console.log('Availability status:', data.availabilityStatus);
                console.log('Providers:', data.providers);
                setMedia(data);
                setMyRating(data.myRating ?? null);
                setAluvoRating(data.aluvoRating ?? null);
                setRatingError(null);
                setDetailsViewer(user ? String(user.id ?? user._id ?? 'user') : 'guest');
                setSeriesMasteryProgress(data.seriesMasteryProgress || null);
                if (!user) {
                    trackEvent(ANALYTICS_EVENTS.GUEST_MEDIA_VIEWED, {
                        platform: 'web',
                        content_type: kind,
                        content_id: tmdbId,
                    });
                }

                // Auto-select the Coin tab when the user already has a coin
                if (kind !== 'tv' && data.userCoin) {
                    setActiveTab('coins');
                }

                if (kind === 'tv') {
                    if (seasonNumber != null) setActiveTab('seasons');
                    try {
                        const seasonsData = await MediaService.getTvSeasons(tmdbId);
                        setSeasons(seasonsData || []);
                    } catch (seasonsErr) {
                        console.error('Failed to load seasons:', seasonsErr);
                        setSeasons([]);
                    }
                }
            } catch (err) {
                console.error('Failed to load media:', err);
                setError(err.message || 'Failed to load media details');
            } finally {
                setLoading(false);
            }
        };

        if (kind && tmdbId) {
            loadMedia();
        }
    }, [kind, tmdbId, user, seasonNumber]);

    useEffect(() => {
        const checkWatchlist = async () => {
            if (!user) {
                setInWatchlist(false);
                return;
            }

            try {
                const watchlist = await WatchlistService.getWatchlist();
                const isInList = watchlist.items?.some(
                    item => item.media?.tmdb_id === Number(tmdbId)
                );
                setInWatchlist(isInList);
            } catch (err) {
                console.error('Failed to check watchlist:', err);
            }
        };

        checkWatchlist();
    }, [user, tmdbId]);

    useEffect(() => {
        CuratedListsService.getForMedia(kind, tmdbId, { limit: 3 })
            .then((data) => setFeaturedLists(data.curatedLists || []))
            .catch(() => setFeaturedLists([]));
    }, [kind, tmdbId]);

    const doWatchlistToggle = useCallback(async () => {
        setWatchlistLoading(true);
        try {
            if (inWatchlist) {
                const watchlist = await WatchlistService.getWatchlist();
                const watchlistItem = watchlist.items?.find(
                    item => item.media?.tmdb_id === Number(tmdbId)
                );
                if (watchlistItem) {
                    await WatchlistService.removeFromWatchlist(watchlistItem.id);
                    setInWatchlist(false);
                }
            } else {
                await WatchlistService.addToWatchlist(Number(tmdbId), kind);
                setInWatchlist(true);
            }
        } catch (err) {
            alert('Failed to update watchlist. Please try again.');
        } finally {
            setWatchlistLoading(false);
        }
    }, [inWatchlist, kind, tmdbId]);

    // Secondary "Add to List" action — the Watchlist button above stays one tap.
    const openSaveToList = useCallback((seasonNumber = null) => {
        setSaveToListTarget({ mediaId: Number(tmdbId), mediaType: kind, seasonNumber, title: media?.title });
    }, [tmdbId, kind, media?.title]);

    const handleAddToList = useCallback((seasonNumber = null) => {
        gateAction(() => openSaveToList(seasonNumber), AUTH_MESSAGES.MY_LISTS, {
            type: 'ADD_TO_LIST',
            returnUrl: window.location.pathname + window.location.search + window.location.hash,
            tmdbId: Number(tmdbId),
            kind,
            seasonNumber,
        })();
    }, [gateAction, openSaveToList, kind, tmdbId]);

    const handleWatchlistToggle = useCallback(() => {
        gateAction(doWatchlistToggle, AUTH_MESSAGES.WATCHLIST, {
            type: inWatchlist ? 'REMOVE_FROM_WATCHLIST' : 'ADD_TO_WATCHLIST',
            returnUrl: window.location.pathname + window.location.search + window.location.hash,
            tmdbId: Number(tmdbId),
            kind,
        })();
    }, [gateAction, doWatchlistToggle, inWatchlist, kind, tmdbId]);

    // ── Star rating ───────────────────────────────────────────────────────────
    const ratingParams = useCallback((extra = {}) => ({
        media_type: 'movie',
        media_id: Number(tmdbId),
        source_screen: 'media_detail',
        has_coin: !!media?.userCoin,
        ...extra,
    }), [tmdbId, media?.userCoin]);

    const handleRatingInteract = useCallback(() => {
        if (ratingStartedRef.current) return;
        ratingStartedRef.current = true;
        trackEvent(ANALYTICS_EVENTS.RATING_STARTED, ratingParams({ has_existing_rating: myRating != null }));
    }, [ratingParams, myRating]);

    // intentExpiresAt: set when replaying a guest's stored intent, so a DOB
    // detour can re-queue it without extending its original deadline.
    const doRate = useCallback(async (value, { intentExpiresAt } = {}) => {
        if (ratingSaving || !isValidRating(value)) return;
        const previous = myRating;
        const previousAggregate = aluvoRating;
        setMyRating(value);
        setAluvoRating(optimisticAggregate(previousAggregate, previous, value));
        setRatingError(null);
        setRatingSaving(true);
        try {
            const res = await TitleRatingService.rate('movie', Number(tmdbId), value);
            setMyRating(res?.myRating ?? value);
            if (res?.aluvoRating) setAluvoRating(res.aluvoRating);
            // `created` comes from the server, so a resumed guest rating on an
            // account that had already rated is reported as an update.
            trackEvent(
                res?.created ? ANALYTICS_EVENTS.RATING_SUBMITTED : ANALYTICS_EVENTS.RATING_UPDATED,
                ratingParams({ rating: res?.myRating ?? value, ...(res?.created ? {} : { previous_rating: previous ?? undefined }) }),
            );
        } catch (err) {
            setMyRating(previous);
            setAluvoRating(previousAggregate);
            if (err?.payload?.requiresDob) {
                // The API layer is already taking them to the DOB step. Keep the
                // rating as the pending intent: EnterDob returns here and the
                // replay below saves it once — on the next visit, never again on
                // this page instance (that would resend it before DOB is done).
                resumedIntentRef.current = true;
                savePendingIntent({
                    type: RATE_TITLE_INTENT,
                    returnUrl: window.location.pathname + window.location.search + window.location.hash,
                    tmdbId: Number(tmdbId),
                    kind,
                    rating: value,
                }, { expiresAt: intentExpiresAt });
                return;
            }
            setRatingError({ message: ratingErrorMessage(err, 'save'), retry: () => doRateRef.current?.(value) });
        } finally {
            setRatingSaving(false);
        }
    }, [ratingSaving, myRating, aluvoRating, tmdbId, kind, ratingParams]);

    const doRemoveRating = useCallback(async () => {
        if (ratingSaving || myRating == null) return;
        const previous = myRating;
        const previousAggregate = aluvoRating;
        setMyRating(null);
        setAluvoRating(optimisticAggregate(previousAggregate, previous, null));
        setRatingError(null);
        setRatingSaving(true);
        try {
            const res = await TitleRatingService.remove('movie', Number(tmdbId));
            if (res?.aluvoRating) setAluvoRating(res.aluvoRating);
            if (res?.removed !== false) trackEvent(ANALYTICS_EVENTS.RATING_REMOVED, ratingParams({ previous_rating: previous }));
        } catch (err) {
            setMyRating(previous);
            setAluvoRating(previousAggregate);
            setRatingError({ message: ratingErrorMessage(err, 'remove'), retry: () => doRemoveRatingRef.current?.() });
        } finally {
            setRatingSaving(false);
        }
    }, [ratingSaving, myRating, aluvoRating, tmdbId, ratingParams]);

    // Retry buttons call the latest handler, not the one captured at failure.
    const doRateRef = useRef(doRate);
    const doRemoveRatingRef = useRef(doRemoveRating);
    useEffect(() => { doRateRef.current = doRate; doRemoveRatingRef.current = doRemoveRating; }, [doRate, doRemoveRating]);

    const handleRate = useCallback((value) => {
        gateAction(() => doRate(value), AUTH_MESSAGES.RATING, {
            type: RATE_TITLE_INTENT,
            returnUrl: window.location.pathname + window.location.search + window.location.hash,
            tmdbId: Number(tmdbId),
            kind,
            rating: value,
        }, { reason: 'rating' })();
    }, [gateAction, doRate, kind, tmdbId]);

    // Every quiz start on web goes through /verify/… (pages/VerifyQuiz), which
    // owns the DOB / email-verification gates, analytics and the single
    // POST /quiz/start call. This page only navigates there.
    const doVerify = useCallback(
        (seasonNumber = null) => {
            const path = buildVerifyPath(kind, Number(tmdbId), seasonNumber);
            if (!path) return;
            setQuizLoading(true);
            navigate(path, { state: { sourceScreen: 'media_detail', mediaTitle: media?.title ?? null } });
        },
        [kind, tmdbId, navigate, media]
    );

    const handleVerify = useCallback((seasonNumber = null) => {
        gateAction(() => doVerify(seasonNumber), AUTH_MESSAGES.QUIZ, {
            type: 'START_VERIFICATION',
            returnUrl: window.location.pathname + window.location.search + window.location.hash,
            tmdbId: Number(tmdbId),
            kind,
            seasonNumber,
        })();
    }, [gateAction, doVerify, kind, tmdbId]);

    useEffect(() => {
        if (!user || !media || resumedIntentRef.current) return;
        const raw = sessionStorage.getItem('aluvo_pending_intent');
        if (!raw) return;

        try {
            const intent = JSON.parse(raw);
            // A rating waits for the details reloaded as this user (their own
            // current rating), so the optimistic save can't be overwritten by
            // the in-flight guest → user reload.
            if (intent.type === RATE_TITLE_INTENT && detailsViewer === 'guest') return;
            const currentUrl = window.location.pathname + window.location.search + window.location.hash;
            const matchesPage = intent.returnUrl === currentUrl && String(intent.tmdbId) === String(tmdbId) && intent.kind === kind;
            if (!matchesPage || (intent.expiresAt && intent.expiresAt < Date.now())) {
                sessionStorage.removeItem('aluvo_pending_intent');
                return;
            }

            resumedIntentRef.current = true;
            sessionStorage.removeItem('aluvo_pending_intent');
            if (intent.type === 'ADD_TO_WATCHLIST' && !inWatchlist) {
                doWatchlistToggle();
            }
            if (intent.type === 'START_VERIFICATION') {
                doVerify(intent.seasonNumber ?? null);
            }
            if (intent.type === 'ADD_TO_LIST') {
                openSaveToList(intent.seasonNumber ?? null);
            }
            // The guest picked this value before signing in; it is saved only
            // now, after sign-in (and the DOB / onboarding steps) completed.
            if (intent.type === RATE_TITLE_INTENT && kind === 'movie' && isValidRating(intent.rating)) {
                doRate(intent.rating, { intentExpiresAt: intent.expiresAt });
            }
        } catch {
            sessionStorage.removeItem('aluvo_pending_intent');
        }
    }, [user, media, tmdbId, kind, inWatchlist, doWatchlistToggle, doVerify, openSaveToList, detailsViewer, doRate]);

    const getTimeUntilRetry = (nextRetryAt) => {
        if (!nextRetryAt) return null;

        const now = new Date();
        const retry = new Date(nextRetryAt);
        const diffMs = retry - now;

        if (diffMs <= 0) return 'Now';

        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    };

    const getRarityIcon = (rarity) => {
        switch (rarity) {
            case 'gold': return '🥇';
            case 'silver': return '🥈';
            case 'bronze': return '🥉';
            default: return '🪙';
        }
    };

    const getRarityColor = (rarity) => {
        switch (rarity) {
            case 'gold': return '#FFD700';
            case 'silver': return '#C0C0C0';
            case 'bronze': return '#CD7F32';
            default: return '#888';
        }
    };

    // ── Live countdown ────────────────────────────────────────────────────────
    function formatCountdown(targetDate) {
        if (!targetDate) return '';
        const diff = new Date(targetDate) - Date.now();
        if (diff <= 0) return 'Now';
        const h = Math.floor(diff / (1000 * 60 * 60));
        const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((diff % (1000 * 60)) / 1000);
        if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
        if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
        return `${s}s`;
    }

    useEffect(() => {
        const nextRetryAt = media?.userCoin?.nextRetryAt;
        if (!nextRetryAt) {
            setCountdown('');
            return;
        }
        // Tick immediately, then every second
        setCountdown(formatCountdown(nextRetryAt));
        countdownRef.current = setInterval(() => {
            const formatted = formatCountdown(nextRetryAt);
            setCountdown(formatted);
            if (formatted === 'Now') clearInterval(countdownRef.current);
        }, 1000);
        return () => clearInterval(countdownRef.current);
    }, [media?.userCoin?.nextRetryAt]);

    // Arrivals from a shared media link (utm_campaign=media_share). First-touch
    // attribution already stores the UTM tag on load.
    useEffect(() => {
        if (media) trackSharedMediaLinkOpened({ kind, tmdbId, seasonNumber });
    }, [media, kind, tmdbId, seasonNumber]);

    useEffect(() => () => clearTimeout(shareStatusTimerRef.current), []);

    const showShareStatus = useCallback((text) => {
        clearTimeout(shareStatusTimerRef.current);
        setShareStatus(text);
        shareStatusTimerRef.current = setTimeout(() => setShareStatus(null), 2500);
    }, []);

    // Media share ("check out this title") — for guests and signed-in users,
    // independent of coin or watchlist state. Coin sharing lives elsewhere.
    const handleShare = useCallback(async () => {
        if (!media || sharing) return;
        const season = seasonNumber != null ? seasons.find((s) => String(s.seasonNumber) === String(seasonNumber)) : null;
        setSharing(true);
        try {
            const outcome = await shareMedia(
                { kind, tmdbId, title: media.title, year: media.year, seasonNumber: seasonNumber ?? null, seasonName: season?.name },
                { sourceScreen: seasonNumber != null ? 'season_detail' : 'media_detail' },
            );
            if (outcome === 'copied') showShareStatus('Link copied');
            else if (outcome === 'error') showShareStatus('Couldn\u2019t share right now.');
        } finally {
            setSharing(false);
        }
    }, [kind, media, seasonNumber, seasons, sharing, showShareStatus, tmdbId]);

    const handleBack = useCallback(() => {
        // A shared link opened in a fresh tab has no in-app history to return to.
        if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
        else navigate('/discover');
    }, [navigate]);

    if (loading) return <p className={styles.loadingText}>Loading…</p>;
    if (error) return <p className={styles.loadingText}>Error: {error}</p>;
    if (!media) return <p className={styles.loadingText}>Not found.</p>;

    const isTV = kind === 'tv';
    const userCoin = media.userCoin;
    const hasCoin = !!userCoin;
    const canUpgrade = hasCoin && userCoin.rarity !== 'legendary' && userCoin.canRetry;
    const isOnCooldown = hasCoin && !userCoin.canRetry && userCoin.nextRetryAt;
    const communityLine = communityRatingView(aluvoRating);

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <div className={styles.headerBar}>
                    <button type="button" className={styles.headerBtn} onClick={handleBack} aria-label="Back">
                        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        className={styles.headerBtn}
                        onClick={handleShare}
                        disabled={sharing}
                        aria-label={`Share ${media.title}${seasonNumber != null ? ` Season ${seasonNumber}` : ''}`}
                        title="Share"
                    >
                        <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                        </svg>
                    </button>
                </div>
                <div className={styles.shareStatus} role="status" aria-live="polite">{shareStatus}</div>
                <div className={styles.hero}>
                    {media.posterUrl && (
                        <img src={media.posterUrl} alt={media.title} className={styles.poster} />
                    )}
                    <div className={styles.heroInfo}>
                        <span className={styles.kindPill}>{isTV ? '📺 TV Show' : '🎬 Movie'}</span>
                        <h1 className={styles.title}>{media.title}</h1>
                        {media.year && <span className={styles.year}>{media.year}</span>}
                        {media.genres?.length > 0 && (
                            <ul className={styles.genres} aria-label="Genres">
                                {media.genres.map((g) => {
                                    const label = typeof g === 'string' ? g : g.name;
                                    return <li key={typeof g === 'string' ? g : (g.id ?? g.name)} className={styles.genrePill}>{label}</li>;
                                })}
                            </ul>
                        )}

                        {!isTV && communityLine && (
                            <p className={styles.communityRating} data-testid="aluvo-community-rating">
                                {communityLine.showAverage ? (
                                    <>
                                        <span className={styles.communityLabel}>Aluvo</span>{' '}
                                        <span className={styles.communityStar} aria-hidden="true">★</span>{' '}
                                        <strong className={styles.communityAverage}>{communityLine.average}</strong>
                                        <span className={styles.communityCount}> · {communityLine.countText}</span>
                                        <span className={styles.srOnly}>{` — Aluvo community rating ${communityLine.average} out of 5`}</span>
                                    </>
                                ) : (
                                    <span className={styles.communityCount}>{communityLine.text}</span>
                                )}
                            </p>
                        )}

                        <p className={styles.overview}>{media.overview}</p>

                        <div className={styles.heroActions}>
                            <button
                                className={`${styles.watchlistBtn} ${inWatchlist ? styles.inWatchlist : ''}`}
                                onClick={handleWatchlistToggle}
                                disabled={watchlistLoading}
                            >
                                {watchlistLoading ? (
                                    '⏳ Loading...'
                                ) : inWatchlist ? (
                                    '✓ In Watchlist'
                                ) : (
                                    '+ Add to Watchlist'
                                )}
                            </button>
                            <button
                                type="button"
                                className={styles.listBtn}
                                onClick={() => handleAddToList(null)}
                                aria-label="Add to List"
                            >
                                ☰ Add to List
                            </button>
                        </div>

                        {!isTV && (
                            <div className={styles.yourRating}>
                                <StarRating
                                    value={myRating}
                                    onChange={handleRate}
                                    onRemove={doRemoveRating}
                                    onInteract={handleRatingInteract}
                                    busy={ratingSaving}
                                    label={user ? 'Your rating' : 'Rate this movie'}
                                    hint={!user ? 'Sign in to add your rating.' : null}
                                />
                                {ratingError && (
                                    <p className={styles.ratingError} role="alert">
                                        {ratingError.message}{' '}
                                        <button type="button" className={styles.ratingRetry} onClick={ratingError.retry} disabled={ratingSaving}>
                                            Try again
                                        </button>
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Series Mastery Progress (TV Shows only) */}
                {isTV && seriesMasteryProgress && (
                    <div className={
                        seriesMasteryProgress.hasMasteryCoin
                            ? styles.masteryProgressComplete
                            : styles.masteryProgressContainer
                    }>
                        {seriesMasteryProgress.hasMasteryCoin ? (
                            <div className={styles.masteryComplete}>
                                <span className={styles.masteryIcon}>🏆</span>
                                <div className={styles.masteryTextContent}>
                                    <strong className={styles.masteryTitle}>Series Mastery Complete!</strong>
                                    <p className={styles.masterySub}>You've earned the ultimate series coin</p>
                                </div>
                            </div>
                        ) : (
                            <div className={styles.masteryInProgress}>
                                <div className={styles.masteryHeader}>
                                    <span className={styles.masteryIcon}>🎯</span>
                                    <div className={styles.masteryTextContent}>
                                        <strong className={styles.masteryTitle}>Series Mastery Progress</strong>
                                        <p className={styles.masterySub}>
                                            {seriesMasteryProgress.verifiedSeasons} of {seriesMasteryProgress.requiredSeasons} seasons verified
                                        </p>
                                    </div>
                                </div>

                                <div className={styles.progressBarContainer}>
                                    <div
                                        className={styles.progressBarFill}
                                        style={{
                                            width: `${(seriesMasteryProgress.verifiedSeasons / seriesMasteryProgress.requiredSeasons) * 100}%`
                                        }}
                                    />
                                </div>

                                <p className={styles.masteryRemaining}>
                                    {seriesMasteryProgress.remainingSeasons === 0
                                        ? '✨ Ready to claim Series Mastery Coin!'
                                        : `🪙 ${seriesMasteryProgress.remainingSeasons} more season${seriesMasteryProgress.remainingSeasons !== 1 ? 's' : ''} to unlock Series Mastery Coin`
                                    }
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <CastSection
                    cast={media.cast}
                    media={media}
                    kind={kind}
                    onFullCastOpened={({ mediaId, mediaType, mediaTitle, castCount }) => trackEvent(
                        ANALYTICS_EVENTS.MEDIA_FULL_CAST_OPENED,
                        { media_id: mediaId, media_type: mediaType, media_title: mediaTitle, source: 'web', cast_count: castCount }
                    )}
                />

                {featuredLists.length > 0 && (
                    <section className={styles.featuredIn}>
                        <h2>Featured in</h2>
                        <div className={styles.featuredListLinks}>
                            {featuredLists.map((list) => {
                                const cover = getCuratedListCover(list);
                                return (
                                    <button
                                        key={list.id}
                                        onClick={() => navigate(`/discover/lists/${list.slug}`)}
                                        className={styles.featuredListLink}
                                    >
                                        {cover.url && <img src={cover.url} alt={cover.alt} />}
                                        <span>{list.title}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Tabs Navigation */}
                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab} ${activeTab === 'watch' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('watch')}
                    >
                        📺 Where to Watch
                    </button>
                    {isTV ? (
                        <button
                            className={`${styles.tab} ${activeTab === 'seasons' ? styles.activeTab : ''}`}
                            onClick={() => setActiveTab('seasons')}
                        >
                            📋 Seasons
                        </button>
                    ) : (
                        <button
                            className={`${styles.tab} ${activeTab === 'coins' ? styles.activeTab : ''}`}
                            onClick={() => setActiveTab('coins')}
                        >
                            🪙 Coin {hasCoin ? '(1)' : ''}
                        </button>
                    )}
                </div>

                {/* Where to Watch Tab */}
                {activeTab === 'watch' && (
                    <section className={styles.tabContent}>
                        {media.providers && media.providers.length > 0 ? (
                            <ProviderSection
                                providers={media.providers}
                                primaryProvider={media.primaryProvider}
                                tmdbId={tmdbId}
                                kind={kind}
                                seasonNumber={null}
                                title={media.title}
                                availabilityStatus={media.availabilityStatus ?? null}
                            />
                        ) : (
                            <ProviderSection
                                providers={[]}
                                primaryProvider={null}
                                tmdbId={tmdbId}
                                kind={kind}
                                seasonNumber={null}
                                title={media.title}
                                availabilityStatus={media.availabilityStatus ?? null}
                            />
                        )}
                    </section>
                )}

                {/* Coins Tab (Movies Only) */}
                {!isTV && activeTab === 'coins' && (
                    <section className={styles.tabContent}>
                        {user ? (
                            <div className={styles.verifyBlock}>
                                {hasCoin && (
                                    <div className={styles.coinStatus}>
                                        <div className={styles.coinHeader}>
                                            <div>
                                                <strong className={styles.coinTitle}>
                                                    {userCoin.rarity.charAt(0).toUpperCase() + userCoin.rarity.slice(1)} Coin Earned!
                                                </strong>
                                                <p className={styles.coinMeta}>
                                                    {userCoin.rarity === 'gold' || userCoin.rarity === 'legendary'
                                                        ? 'Upgraded '
                                                        : 'Earned '}
                                                    {new Date(userCoin.watchedAt).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>

                                        {userCoin.coinImageUrl && (
                                            <img
                                                src={userCoin.coinImageUrl}
                                                alt={`${userCoin.rarity} coin`}
                                                className={styles.coinImage}
                                            />
                                        )}
                                    </div>
                                )}

                                {hasCoin && <MediaCollectorStats stats={media.collectorStats} />}

                                {!hasCoin && (
                                    <>
                                        <div className={styles.verifyExplainer}>
                                            <span className={styles.verifyExplainerIcon}>🪙</span>
                                            <div>
                                                <strong>Watched it? Prove it.</strong>
                                                <p>Pass a short challenge to verify your watch and earn a collectible coin for this title.</p>
                                            </div>
                                        </div>
                                        <MediaCollectorStats stats={media.collectorStats} />
                                        {/* Same decision as quiz start (GET /media/details quizAvailability). */}
                                        {media.quizAvailability?.quizReady === false && media.quizAvailability.reason === 'quiz_coming_soon' ? (
                                            <p className={styles.verifyExplainer} role="status">
                                                ⏳ Challenge coming soon
                                                {media.quizAvailability.availableAtFormatted ? ` — unlocks ${media.quizAvailability.availableAtFormatted}` : ''}
                                            </p>
                                        ) : (
                                            <button
                                                className={styles.verifyBtn}
                                                onClick={() => handleVerify(null)}
                                                disabled={quizLoading}
                                            >
                                                {quizLoading ? '🔄 Getting Challenge...' : 'Verify Watch & Earn Coin'}
                                            </button>
                                        )}
                                    </>
                                )}

                                {canUpgrade && (
                                    <>
                                        <div className={styles.upgradeInfo}>
                                            <span className={styles.upgradeIcon}>⬆️</span>
                                            <div>
                                                <strong>Upgrade Available!</strong>
                                                <p>Take another challenge to try for a {userCoin.rarity === 'bronze' ? 'silver or gold' : userCoin.rarity === 'silver' ? 'gold' : 'legendary'} coin.</p>
                                            </div>
                                        </div>
                                        <button
                                            className={styles.upgradeBtn}
                                            onClick={() => handleVerify(null)}
                                            disabled={quizLoading}
                                        >
                                            {quizLoading ? '🔄 Getting Challenge...' : 'Retake Challenge for Upgrade'}
                                        </button>
                                    </>
                                )}

                                {isOnCooldown && (
                                    <>
                                        <div className={styles.cooldownInfo}>
                                            <span className={styles.cooldownIcon}>⏰</span>
                                            <div>
                                                <strong>
                                                    {userCoin.rarity === 'gold'
                                                        ? 'Legendary attempt locked'
                                                        : 'Cooldown Active'}
                                                </strong>
                                                <p>
                                                    {userCoin.rarity === 'gold'
                                                        ? 'Legendary attempt unlocks in '
                                                        : 'Upgrade attempt unlocks in '}
                                                    <strong className={styles.countdownValue}>
                                                        {countdown || '…'}
                                                    </strong>
                                                </p>
                                            </div>
                                        </div>
                                        {userCoin.rarity === 'gold' && (
                                            <AppPromotionCard
                                                variant="post_gold"
                                                compact
                                            />
                                        )}
                                    </>
                                )}

                                {hasCoin && userCoin.rarity === 'legendary' && (
                                    <div className={styles.maxRarityInfo}>
                                        <span className={styles.maxRarityIcon}>✦</span>
                                        <div>
                                            <strong>Legendary!</strong>
                                            <p>You've reached the highest tier for this title.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className={styles.verifyBlock}>
                                <div className={styles.verifyExplainer}>
                                    <span className={styles.verifyExplainerIcon}>🪙</span>
                                    <div>
                                        <strong>Watched it? Prove it.</strong>
                                        <p>Sign in to verify your watch and earn a collectible coin for this title.</p>
                                    </div>
                                </div>
                                <MediaCollectorStats stats={media.collectorStats} />
                                <button
                                    className={styles.verifyBtn}
                                    onClick={() => handleVerify(null)}
                                >
                                    Sign In to Earn Coin
                                </button>
                            </div>
                        )}
                    </section>
                )}

                {/* Seasons Tab (TV Shows Only) */}
                {isTV && activeTab === 'seasons' && (
                    <section className={styles.tabContent}>
                        <h2 className={styles.sectionTitle}>Seasons</h2>
                        <p className={styles.sectionSub}>
                            Select a season you've watched. Pass the challenge to verify your watch and earn a coin for that season.
                        </p>
                        <SeasonsList
                            tmdbId={tmdbId}
                            onVerify={(seasonNumber) => handleVerify(seasonNumber)}
                            onAddSeasonToList={(seasonNumber) => handleAddToList(seasonNumber)}
                            earnedSeasons={seasons
                                .filter(s => s.userCoin)
                                .map(s => s.seasonNumber)
                            }
                        />
                    </section>
                )}
            </div>
            <SaveToListModal
                isOpen={!!saveToListTarget}
                target={saveToListTarget}
                source="media_detail"
                onClose={() => setSaveToListTarget(null)}
                onSaved={(_membership, { watchlistChanged, inWatchlist: nowInWatchlist }) => {
                    if (watchlistChanged) setInWatchlist(nowInWatchlist);
                }}
            />
            <AuthModal {...authModalProps} />
        </div>
    );
}
